import { decorateIcons } from 'scripts/aem.js';
import type { Placeholders } from 'utils/fetchPlaceholders.ts';
import './search-box.css';

function searchButton(
	placeholders: Placeholders,
	onSearch: (value: string) => void,
	inputId: string,
) {
	const button = document.createElement('button');
	button.className = 'search-box__button small';
	button.innerText = placeholders.searchButton || 'Search';
	button.type = 'submit';
	button.addEventListener('click', () => {
		const form = button.closest('form');
		const input = form?.querySelector(`#${inputId}`) as HTMLInputElement | null;
		const searchText = input?.value ?? '';
		onSearch(searchText ?? '');
	});
	return button;
}

type SearchInputProps = {
	placeholders: Placeholders;
	onSearch: (value: string) => void;
	onClear: () => void;
	inputId: string;
	autoComplete?: boolean;
	autoFocusInput?: boolean;
};
function searchInput({
	placeholders,
	onSearch,
	onClear,
	inputId,
	autoComplete,
	autoFocusInput,
}: SearchInputProps) {
	const input = document.createElement('input');
	input.className = 'search-input';
	input.id = inputId;
	input.type = 'search';
	if (autoFocusInput) input.autofocus = true;

	const inputPlaceholder = placeholders.searchInputPlaceholder || 'Search';
	input.placeholder = inputPlaceholder;
	input.ariaLabel = inputPlaceholder;

	input.addEventListener('keyup', (e) => {
		// match on universal alphanumeric characters
		const regExp =
			/^([a-zA-Z0-9\u0600-\u06FF\u0660-\u0669\u06F0-\u06F9 _.-]+)$/;
		if (e.code === 'Escape') {
			onClear();
		} else if (e.key === 'Enter') {
			// avoid unnecessary typeahead on enter key
			return;
		} else if (autoComplete && regExp.test(e.key)) {
			onSearch((e.target as HTMLInputElement)?.value);
		}
	});

	return input;
}

function searchIcon() {
	const icon = document.createElement('span');
	icon.classList.add('icon', 'icon-search-black');
	return icon;
}

type SearchBoxProps = {
	placeholders: Placeholders;
	onSearch: (value: string) => void;
	onSubmit?: () => void;
	onClear: () => void;
	inputIdOverride?: string;
	autoComplete?: boolean;
	autoFocusInput?: boolean;
};
export function searchBox({
	placeholders,
	onSearch,
	onSubmit,
	onClear,
	inputIdOverride,
	autoComplete = false,
	autoFocusInput = false,
}: SearchBoxProps) {
	const inputId = inputIdOverride ?? 'search-input';
	const box = document.createElement('form');
	box.setAttribute('autocomplete', 'off');
	box.addEventListener('submit', (e: SubmitEvent) => {
		// prevent the page from refreshing on submit
		e.preventDefault();
		if (onSubmit) {
			onSubmit();
		}
	});
	box.classList.add('search-box');
	box.append(
		searchIcon(),
		searchInput({
			placeholders,
			onSearch,
			onClear,
			inputId,
			autoComplete,
			autoFocusInput,
		}),
		searchButton(placeholders, onSearch, inputId),
	);

	decorateIcons(box);
	return box;
}
